// E2E encryption for direct messages (ECDH P-256 + AES-GCM via Web Crypto API)
// Multi-device: private key backed up on server encrypted with the user's password (PBKDF2) — server cannot read it
import { api } from "@/App";

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0)).buffer;
const PBKDF2_ITERATIONS = 310000;

export function getLocalKeyPair(userId) {
  const priv = localStorage.getItem(`ade_e2e_priv_${userId}`);
  const pub = localStorage.getItem(`ade_e2e_pub_${userId}`);
  return priv && pub ? { priv: JSON.parse(priv), pub } : null;
}

function storeLocalKeyPair(userId, privJwk) {
  const pubStr = JSON.stringify({ kty: privJwk.kty, crv: privJwk.crv, x: privJwk.x, y: privJwk.y });
  localStorage.setItem(`ade_e2e_priv_${userId}`, JSON.stringify(privJwk));
  localStorage.setItem(`ade_e2e_pub_${userId}`, pubStr);
  return pubStr;
}

async function generateKeyPair(userId) {
  const kp = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveKey"]
  );
  const privJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  const pubStr = storeLocalKeyPair(userId, privJwk);
  await api.post("/chat/keys", { public_key: pubStr });
  return { priv: privJwk, pub: pubStr };
}

// Returns keypair, or null if a server backup exists that needs a password restore
export async function ensureKeyPair(userId) {
  const local = getLocalKeyPair(userId);
  if (local) {
    api.post("/chat/keys", { public_key: local.pub }).catch(() => {});
    return local;
  }
  try {
    const { data } = await api.get("/chat/keys/backup");
    if (data.backup) return null;
  } catch {
    // proceed with generation
  }
  return generateKeyPair(userId);
}

async function deriveWrappingKey(password, saltBuf) {
  const base = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: saltBuf, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function createBackup(userId, password) {
  let local = getLocalKeyPair(userId);
  if (!local) local = await generateKeyPair(userId);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveWrappingKey(password, salt);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(JSON.stringify(local.priv))
  );
  await api.put("/chat/keys/backup", { ciphertext: b64(ct), salt: b64(salt.buffer), iv: b64(iv.buffer) });
}

export async function restoreFromBackup(userId, password) {
  const { data } = await api.get("/chat/keys/backup");
  if (!data.backup) throw new Error("no-backup");
  const { ciphertext, salt, iv } = data.backup;
  const key = await deriveWrappingKey(password, new Uint8Array(fromB64(salt)));
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(fromB64(iv)) },
    key,
    fromB64(ciphertext)
  );
  const privJwk = JSON.parse(new TextDecoder().decode(pt));
  const pubStr = storeLocalKeyPair(userId, privJwk);
  await api.post("/chat/keys", { public_key: pubStr });
  return { priv: privJwk, pub: pubStr };
}

// Called at login/registration when the password is available
export async function syncKeysOnLogin(userId, password) {
  try {
    const { data } = await api.get("/chat/keys/backup");
    const local = getLocalKeyPair(userId);
    if (data.backup && !local) {
      await restoreFromBackup(userId, password);
    } else {
      await createBackup(userId, password);
    }
  } catch (e) {
    console.error("E2E key sync failed", e);
  }
}

export async function deriveSharedKey(myPrivJwk, theirPubJwkStr) {
  const priv = await crypto.subtle.importKey(
    "jwk",
    myPrivJwk,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveKey"]
  );
  const pub = await crypto.subtle.importKey(
    "jwk",
    JSON.parse(theirPubJwkStr),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    []
  );
  return crypto.subtle.deriveKey(
    { name: "ECDH", public: pub },
    priv,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptText(key, text) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(text)
  );
  return { content: b64(ct), iv: b64(iv.buffer) };
}

export async function decryptText(key, contentB64, ivB64) {
  try {
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(fromB64(ivB64)) },
      key,
      fromB64(contentB64)
    );
    return new TextDecoder().decode(pt);
  } catch {
    return null;
  }
}
